import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { LineService } from "@services/line.service";
import { Line } from "@models/line.model";
import { TableConfig } from "@app/shared/util/table-config.utils";
import { ToolService } from "@app/core/services/tool.service";
import { ConfirmationModalComponent } from "@components/confirmation-modal/confirmation-modal.component";
import { ToolSet } from "@app/tool/model/toolSet.model";
import { ToolRequestsService } from "@app/core/services/tool-requests.service";
import { Log } from "ng2-logger";
import { TranslateService } from "@ngx-translate/core";
import { Tool } from "@app/tool/model/tool.model";
import { Group } from "@app/tool/model/group.model";
import { InsertionCycle } from '@app/shared/models/insertion-cycle.model';
import { InsertionCyclesService } from '@app/core/services/insertion-cycles.service';
import { UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ObservableService } from '@app/core/services/observable.service';
import { ToolRequestTableComponent } from "@app/tool/tool-request-table/tool-request-table.component";
import { LogoutModalComponent } from '@app/shared/components';
import { environment } from '../../../../../../environments/environment';

class ToolSteps {
    static SELECT_LINE = 0;
    static SELECT_TOOLS = 1;
    static CONFIRM = 2;
}

@Component({
    selector: 'app-tool-individual-request',
    templateUrl: './tool-individual-request.component.html',
    styleUrls: ['./tool-individual-request.component.scss']
})
export class ToolIndividualRequestComponent implements OnInit, OnDestroy {
    _logger = Log.create(ToolIndividualRequestComponent.name);

    @ViewChild(ToolRequestTableComponent, {static: false}) table: ToolRequestTableComponent;
    @ViewChild('successModal', {static: false}) confirmationModal: ConfirmationModalComponent;
    @ViewChild('errorModal', {static: false}) errorModal: ConfirmationModalComponent;
    @ViewChild(LogoutModalComponent, {static: false}) logoutModal: LogoutModalComponent;

    firstNextTried = false;
    noInsertionCycles = false;

    currentStep = 1;

    lines: Line[] = [];
    selectedLine: Line;

    insertionCycles: InsertionCycle[];
    selectedInsertionCycle: InsertionCycle;

    isLoadingTools = false;
    tableConfig = new TableConfig();
    toolsGrouped = new Array<Group>();
    selectedTools: Array<Tool | ToolSet> = new Array<Tool | ToolSet>();
    toolsToSwitch: Array<Tool | ToolSet> = new Array<Tool | ToolSet>();
    toolsFromAtom: Array<Tool | ToolSet> = new Array<Tool | ToolSet>();

    toolRequestForm: UntypedFormGroup;

    isConfirmationInProgress: boolean;
    orderWithPriority = false;
    confirmationPopupMessage: string;
    isError: boolean;
    hasGiveConsent = false;
    observableServiceSubscription: Subscription;
    showFilters = true;
    allExpanded = false;
    readonly badgeLogoutCountdownSeconds = environment.badgeLogoutCountdownSeconds;

    constructor(private lineService: LineService,
                private toolService: ToolService,
                private insertionCyclesService: InsertionCyclesService,
                private translateService: TranslateService,
                private toolRequestService: ToolRequestsService,
                observableServiceService: ObservableService) {
        this.observableServiceSubscription = observableServiceService.hasChanged$.subscribe(
            () => {
                if (this.currentStep === 2) {
                    this.onStepChange(ToolSteps.SELECT_TOOLS);
                }
            }
        )
    }

    ngOnInit() {
        this._logger.info("ngOnInit:: Retrieving lines");

        this.isLoadingTools = true;
        this.lineService.getAllLines().subscribe(
            (data: any) => {
                this._logger.info("ngOnInit:: API returned data", data);
                this.lines = data.items;
                this.isLoadingTools = false;
            },
            (error: any) => {
                this._logger.error("ngOnInit:: API returned error", error);
                this.isLoadingTools = false;
            }
        );

        this.toolRequestForm = new UntypedFormGroup({
            line: new UntypedFormControl(),
            insertionCycle: new UntypedFormControl(),
            lineSetterConsent: new UntypedFormControl()
        })
    }

    ngOnDestroy() {
        this.observableServiceSubscription.unsubscribe();
    }

    onStepChange(stepIndex) {
        this.firstNextTried = false;
        switch (stepIndex) {
            case ToolSteps.SELECT_LINE:
                this.currentStep = 1;
                break;
            case ToolSteps.SELECT_TOOLS:
                this.selectedInsertionCycle ?
                    this.getToolsForLinePartNumberAndInsertionCycle(this.tableConfig, this.selectedLine.id, this.selectedInsertionCycle.id)
                    : this.getToolsForLinePartNumberAndInsertionCycle(this.tableConfig, this.selectedLine.id);
                this.currentStep = 2;
                break;
            case ToolSteps.CONFIRM:
                this.getToolsThatNeedLineSwitch();
                this.currentStep = 3;
                break;
        }
    }


    onPageChange(tableState) {
        const insertionCycles = this.selectedInsertionCycle ? this.selectedInsertionCycle.id : null;
        this.getToolsForLinePartNumberAndInsertionCycle(tableState, this.selectedLine.id, insertionCycles);
    }

    onSortChange(tableState) {
        const insertionCycles = this.selectedInsertionCycle ? this.selectedInsertionCycle.id : null;
        this.getToolsForLinePartNumberAndInsertionCycle(tableState, this.selectedLine.id, insertionCycles);
    }

    onFilterChange(tableState) {
        const insertionCycles = this.selectedInsertionCycle ? this.selectedInsertionCycle.id : null;
        this.getToolsForLinePartNumberAndInsertionCycle(tableState, this.selectedLine.id, insertionCycles);
    }

    onFilterGroupChange(tableState) {
        const insertionCycles = this.selectedInsertionCycle ? this.selectedInsertionCycle.id : null;
        if (this.selectedLine && tableState.filters && tableState.filters.length > 0) {
            this.getToolsForLinePartNumberAndInsertionCycle(tableState, this.selectedLine.id, insertionCycles);
        }
    }

    onGroupChange(tableState) {
        const insertionCycles = this.selectedInsertionCycle ? this.selectedInsertionCycle.id : null;
        this.getToolsForLinePartNumberAndInsertionCycle(tableState, this.selectedLine.id, insertionCycles);
    }

    onLineSelected(line: Line) {
        if (line && line instanceof Line) {
            this._logger.info("onLineSelected:: ", line);
            this.selectedLine = line;
            this.selectedTools = new Array<Tool | ToolSet>();
            this.insertionCycles = new Array<InsertionCycle>();
            if (this.currentStep === 1) {
                this.toolRequestForm.controls.insertionCycle.setValue(null);
                this.selectedInsertionCycle = null;
            }
            this.getInsertionCyclesByLine();
        }
    }

    onInsertionCycleSelect(insertionCycle: InsertionCycle) {
        if (insertionCycle) {
            this.selectedInsertionCycle = insertionCycle;
        }
    }

    onToolClicked(toolDetails: { checked: boolean, group: Tool | ToolSet }) {
        if (toolDetails.checked) {
            this.selectedTools.push(toolDetails.group);
        } else {
            const index = this.selectedTools.findIndex(item => item.id === toolDetails.group.id)
            if (index > -1) {
                this.selectedTools.splice(index, 1);
            }
        }
        this._logger.info("onToolClicked:: Selected tools", this.selectedTools);
    }

    onTryNext() {
        this.firstNextTried = true;
    }

    onOrder(priority: boolean) {
        this.orderWithPriority = priority;
        this.confirmationPopupMessage = '';
        this.confirmationModal.open();
    }

    getIdsArrayForSelectedTools(selectedTools: Array<Tool | ToolSet>) {
        const ids = [];
        for (const toolItem of selectedTools) {
            if (toolItem instanceof ToolSet) {
                for (const tool of toolItem.tools) {
                    ids.push(tool.id);
                }
            } else {
                ids.push(toolItem.id);
            }
        }
        return ids;
    }

    onConfirmOrder(reason) {
        this.isConfirmationInProgress = true;
        this._logger.info("onConfirmOrder:: Creating individual tool request, Reason:: " + reason);
        this.isError = false;
        this.toolRequestService.createToolRequest(
            this.selectedLine.id,
            null,
            this.orderWithPriority,
            reason,
            this.getIdsArrayForSelectedTools(this.selectedTools),
            this.hasGiveConsent,
            this.selectedInsertionCycle ? this.selectedInsertionCycle.id : null
        ).subscribe(
            data => {
                this.confirmationModal.close();
                this._logger.info("onConfirmOrder:: API Returned data", data);

                if (data.status === 'Closed') {
                    this.confirmationPopupMessage = this.translateService.instant('lineSetter.toolRequest.toasts.toolRequestCreatedClosed');
                } else {
                    this.confirmationPopupMessage = this.translateService.instant('lineSetter.toolRequest.toasts.toolRequestCreated');
                }
                this.isConfirmationInProgress = false;
                this.logoutModal.open();
            },
            error => {
                this.isConfirmationInProgress = false;
                this._logger.error("onConfirmOrder:: API returned error", error);
                if (error && error.error) {
                    this.confirmationPopupMessage = this.translateService.instant(error.error.message);
                    this.isError = true;
                    this.errorModal.open();
                }
                this.confirmationModal.close();
            }
        )
    }

    private getToolsForLinePartNumberAndInsertionCycle(tableConfig = this.tableConfig, lineId: number, insertionCycleId: number = null) {
        this._logger.info("getToolsForLineAndPartNumber:: Retrieving tools for (tableConfig,lineId)",
            tableConfig, lineId, insertionCycleId);
        this.isLoadingTools = true;
        this.toolService.getToolsForLineAndPartNumber(
            this.tableConfig,
            this.selectedLine.id,
            null,
            true,
            insertionCycleId
        ).subscribe(data => {
            this._logger.info("getToolsForLineAndPartNumber:: API returned data", data);
            // TODO : different strategy of disabling fields
            data.tools.forEach((tool: Tool) => {
                tool['disabled'] = tool.isAlreadyAtLine;
            });
            this.toolsGrouped = data.tools;
            this.tableConfig.pager.count = data.count;
            this.isLoadingTools = false;
        });
    }

    private getInsertionCyclesByLine() {
        this.isLoadingTools = true;
        this._logger.info("getInsertionCycles:: Retrieving insertion cycles");
        this.insertionCyclesService.getInsertionCyclesByLinesIds([this.selectedLine.id])
            .subscribe((data: any) => {
                this.insertionCycles = data.items;
                this.noInsertionCycles = this.insertionCycles.length === 0;
                this.isLoadingTools = false;
            });
    }

    private getToolsThatNeedLineSwitch() {
        const ids = this.selectedTools.map(x => x.id);
        this.isLoadingTools = true;
        this.toolsToSwitch = [];
        this.toolsFromAtom = [];
        this.hasGiveConsent = false;
        this.selectedTools.forEach(x => this.toolsFromAtom.push(x));
        this.toolService.getToolsThatNeedLineSwitch(ids, this.selectedLine.id).subscribe((data: any) => {
                if (data && data.length === 0) {
                    this.hasGiveConsent = true;
                    this.isLoadingTools = false;
                    return;
                }

                for (let i = 0; i < data.length; i++) {
                    const tool = this.selectedTools.find(x => x.id === data[i]);
                    if (!tool) {
                        continue;
                    }
                    const index = this.toolsFromAtom.indexOf(tool);
                    if (index >= 0) {
                        this.toolsFromAtom.splice(index, 1);
                    }
                    this.toolsToSwitch.push(tool);
                }


                this.isLoadingTools = false;
            },
            (error: any) => {
                this.isLoadingTools = false;
            });
    }

    canContinue() {
        if (!this.selectedLine || this.isLoadingTools) {
            return false;
        }
        return !(this.selectedLine.isSPI && !this.selectedInsertionCycle);
    }

    toggleFilters() {
        this.showFilters = !this.showFilters;
    }

    toggleExpanded() {
        this.allExpanded = !this.allExpanded;
        this.table.toggleRowExpansions(this.allExpanded);
    }

    onConsent(consent: boolean) {
        this.hasGiveConsent = consent;
    }
}
